import type { PatientAuthContext } from './patientTypes';

// Clean Patient API base. Web + APK MUST use the same value.
// Configured via VITE_PATIENT_API_BASE_URL (see .env.example). Falls back to
// the Patient-owned namespace only — never /api/ecc.
const BASE_URL =
  (import.meta.env?.VITE_PATIENT_API_BASE_URL as string | undefined) || '/api/patient';

const AUTH_STORAGE_KEY = 'nhc_patient_auth_context';
const TOKEN_STORAGE_KEY = 'nhc_patient_token';

export function getAuthContext(): PatientAuthContext | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PatientAuthContext;
  } catch {
    return null;
  }
}

export function setAuthContext(ctx: PatientAuthContext): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(ctx));
}

export function clearAuthContext(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export function setPatientToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function getPatientToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

/**
 * Turn an absolute path the API hands us — a doctor's photo, say — into a URL
 * that reaches the API host.
 *
 * `doctor.photoUrl` arrives as `/api/doctor/profile/photo/<file>`, which is a
 * path on the API, not on this site. Rendering it as-is works behind the dev
 * proxy and breaks in production, where the static host answers every unknown
 * path with `index.html` and a 200 — so the image silently falls back to the
 * initials and nobody sees an error.
 */
export function apiFileUrl(path: string | null | undefined): string | null {
  const p = (path ?? '').trim();
  if (!p || p === 'null') return null;
  if (/^https?:\/\//i.test(p)) return p;
  if (!p.startsWith('/')) return p;
  return `${BASE_URL.replace(/\/api\/patient\/?$/, '')}${p}`;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  const token = getPatientToken();
  if (token) {
    // A verified Bearer token is present — the backend must derive identity
    // and role from the token's claims, not from client-asserted headers.
    // Do not also send x-patient-* identity headers here: sending both
    // invites a backend that (incorrectly) trusts the headers to be spoofed
    // by anyone who edits their own localStorage.
    headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  const ctx = getAuthContext();
  if (ctx) {
    headers['x-patient-user-id'] = ctx.userId;
    headers['x-patient-role'] = ctx.role;
    headers['x-patient-display-name'] = ctx.displayName;
    if (ctx.accountId) headers['x-patient-account-id'] = ctx.accountId;
  }
  return headers;
}

/** Whether the current request carried a Bearer token — i.e. was an authenticated call. */
function isAuthenticatedRequest(): boolean {
  return getPatientToken() !== null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: unknown,
  ) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

const LOGIN_PATHS = ['/patient/entry', '/patient/login', '/login'];

/**
 * A 401 means the session is no longer valid (expired/revoked token). Without
 * this, every page's fetch silently fails and callers render "no data" —
 * indistinguishable from a patient genuinely having no records. Clear the
 * stale session and send them back to log in instead.
 */
function handleUnauthorized(): void {
  clearAuthContext();
  if (typeof window === 'undefined') return;
  const alreadyOnLogin = LOGIN_PATHS.some(p => window.location.pathname.startsWith(p));
  if (!alreadyOnLogin) {
    window.location.href = '/patient/entry';
  }
}

async function handleResponse<T>(response: Response, wasAuthenticated: boolean): Promise<T> {
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text().catch(() => null);
    }
    // A 401 on a request that carried a Bearer token means the session is no
    // longer valid — clear it and send the user back to log in. A 401 on a
    // request with NO token (login, register, send-otp, verify-otp) just
    // means the credentials/code were wrong; that's a normal, expected
    // rejection the caller already handles — it must NOT log the user out or
    // redirect, or every wrong-password/wrong-OTP attempt would bounce
    // straight to the entry screen instead of showing an inline error.
    if (response.status === 401 && wasAuthenticated) {
      handleUnauthorized();
    }
    throw new ApiError(response.status, response.statusText, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const json = await response.json();
  if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
    return json.data as T;
  }
  return json as T;
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const url = `${BASE_URL}${path}`;
  if (!params) return url;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  }

  const qs = searchParams.toString();
  return qs ? `${url}?${qs}` : url;
}

export const patientApi = {
  async get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    const response = await fetch(buildUrl(path, params), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    return handleResponse<T>(response, isAuthenticatedRequest());
  },

  async post<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response, isAuthenticatedRequest());
  },

  async patch<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response, isAuthenticatedRequest());
  },

  async put<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response, isAuthenticatedRequest());
  },

  async del<T = void>(path: string): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    return handleResponse<T>(response, isAuthenticatedRequest());
  },

  /** Multipart upload — the browser sets the Content-Type boundary itself. */
  async upload<T>(path: string, form: FormData): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form,
    });
    return handleResponse<T>(response, isAuthenticatedRequest());
  },

  /** Fetch a stored file as a Blob (e.g. Health Vault document contents). */
  async blob(path: string): Promise<Blob> {
    const response = await fetch(buildUrl(path), {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!response.ok) {
      if (response.status === 401 && isAuthenticatedRequest()) handleUnauthorized();
      throw new ApiError(response.status, response.statusText, null);
    }
    return response.blob();
  },
};


